import { Loader, Tooltip } from '@mantine/core'
import { IconAlertCircle, IconCheck, IconChecks } from '@tabler/icons-react'
import { RECEIPT_LABELS, type ReceiptStatus } from '../receipts'

type MessageReceiptProps = {
  status: ReceiptStatus
  onRetry?: () => void
}

/**
 * Delivery indicator for a message the current practitioner sent. Never
 * rendered on received messages.
 *
 * The tooltip carries the state alone, no timestamp. The only timestamp
 * available for `read`/`delivered` is the participant pointer, which stores the
 * `sent` of the newest message they had seen rather than the moment they saw it
 * (see `service.ts` `lastReadAt`) — so for the newest message it is equal to
 * `message.sent` by construction, and showing it read as "read at the instant
 * it was sent". The send time is already on the bubble's meta line.
 *
 * Accessibility: the glyph is hidden from assistive tech and the state is
 * carried by visually-hidden text instead, so it never depends on the
 * checkmark count or colour. The tooltip opens on hover, keyboard focus, and
 * touch, exposing the same wording.
 */
export const MessageReceipt = ({ status, onRetry }: MessageReceiptProps) => {
  const label = RECEIPT_LABELS[status]

  if (status === 'failed') {
    return (
      // `relative` is load-bearing: Tailwind's `.sr-only` is position:absolute,
      // so without a positioned wrapper its containing block resolves to the
      // app shell — an ancestor of the message scroller, which therefore cannot
      // clip it. The stray 1px spans then extend the document and give the page
      // a phantom scrollbar.
      <span className="relative inline-flex items-center gap-1">
        <IconAlertCircle size={13} aria-hidden className="text-white" />
        <span className="sr-only">{label}</span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="cursor-pointer text-[11px] font-bold text-white underline underline-offset-2"
          >
            Retry
          </button>
        )}
      </span>
    )
  }

  return (
    <Tooltip
      label={label}
      withArrow
      events={{ hover: true, focus: true, touch: true }}
      styles={{ tooltip: { background: '#2B2F36', fontSize: 12, fontWeight: 500 } }}
    >
      {/* Focusable so keyboard users can surface the tooltip too. */}
      <span
        tabIndex={0}
        // `relative` keeps the absolutely-positioned .sr-only child inside the
        // scroller's containing block — see the note in the failed branch.
        className="relative inline-flex items-center rounded-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
      >
        {status === 'sending' && <Loader size={11} color="white" aria-hidden />}
        {status === 'sent' && <IconCheck size={13} aria-hidden className="text-white/80" />}
        {status === 'delivered' && <IconChecks size={13} aria-hidden className="text-white/80" />}
        {status === 'read' && <IconChecks size={13} aria-hidden className="text-[#7FB3FF]" />}
        <span className="sr-only">{label}</span>
      </span>
    </Tooltip>
  )
}
